import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';

interface CryptoRate {
  symbol: string;
  name: string;
  price: number;
  change24h: number;
  icon: string;
}

const CryptoCalculator = () => {
  const navigate = useNavigate();
  const [fromCrypto, setFromCrypto] = useState('PEP');
  const [toCrypto, setToCrypto] = useState('USD');
  const [fromAmount, setFromAmount] = useState('1');
  const [toAmount, setToAmount] = useState('0');
  const [rates, setRates] = useState<Record<string, CryptoRate>>({});
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [error, setError] = useState('');

  const cryptoList: CryptoRate[] = [
    { symbol: 'PEP', name: 'PepCoin', price: 0, change24h: 0, icon: 'ri-coin-line' },
    { symbol: 'BTC', name: 'Bitcoin', price: 0, change24h: 0, icon: 'ri-bit-coin-line' },
    { symbol: 'ETH', name: 'Ethereum', price: 0, change24h: 0, icon: 'ri-ethereum-line' },
    { symbol: 'USDT', name: 'Tether', price: 0, change24h: 0, icon: 'ri-money-dollar-circle-line' },
    { symbol: 'BNB', name: 'Binance Coin', price: 0, change24h: 0, icon: 'ri-currency-line' },
    { symbol: 'SOL', name: 'Solana', price: 0, change24h: 0, icon: 'ri-sun-line' },
    { symbol: 'XRP', name: 'Ripple', price: 0, change24h: 0, icon: 'ri-water-flash-line' },
    { symbol: 'ADA', name: 'Cardano', price: 0, change24h: 0, icon: 'ri-heart-pulse-line' },
    { symbol: 'DOGE', name: 'Dogecoin', price: 0, change24h: 0, icon: 'ri-emotion-happy-line' },
    { symbol: 'DOT', name: 'Polkadot', price: 0, change24h: 0, icon: 'ri-circle-line' },
    { symbol: 'MATIC', name: 'Polygon', price: 0, change24h: 0, icon: 'ri-shape-line' },
    { symbol: 'LINK', name: 'Chainlink', price: 0, change24h: 0, icon: 'ri-links-line' },
    { symbol: 'USD', name: 'US Dollar', price: 1, change24h: 0, icon: 'ri-money-dollar-circle-line' },
    { symbol: 'EUR', name: 'Euro', price: 0, change24h: 0, icon: 'ri-money-euro-circle-line' },
    { symbol: 'GBP', name: 'British Pound', price: 0, change24h: 0, icon: 'ri-money-pound-circle-line' },
  ];

  const fetchPrices = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/crypto-price-fetcher`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.success && data.prices) {
        setRates(data.prices);
        setLastUpdate(new Date());
        setError('');
      } else {
        throw new Error('Failed to fetch prices');
      }
    } catch (err) {
      console.error('Error fetching prices:', err);
      setError('Unable to fetch live prices. Please try again.');
      
      // Fallback to default prices if API fails
      const fallbackRates: Record<string, CryptoRate> = {};
      cryptoList.forEach(crypto => {
        fallbackRates[crypto.symbol] = crypto;
      });
      setRates(fallbackRates);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchPrices();
    
    // Update prices every 30 seconds
    const interval = setInterval(() => {
      fetchPrices();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (rates[fromCrypto] && rates[toCrypto] && fromAmount) {
      const amount = parseFloat(fromAmount) || 0;
      const fromPrice = rates[fromCrypto].price;
      const toPrice = rates[toCrypto].price;
      const result = (amount * fromPrice) / toPrice;
      setToAmount(result.toFixed(8));
    }
  }, [fromAmount, fromCrypto, toCrypto, rates]);

  const handleSwap = () => {
    setFromCrypto(toCrypto);
    setToCrypto(fromCrypto);
    setFromAmount(toAmount);
  };

  const quickAmounts = [100, 500, 1000, 5000, 10000];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-6xl text-cyan-400 animate-spin"></i>
          <p className="text-white mt-4">Loading Live Market Prices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navbar />
      
      {/* Back Button */}
      <div className="pt-20 px-4">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white transition-all duration-300 cursor-pointer group mb-4"
          >
            <i className="ri-arrow-left-line text-lg group-hover:-translate-x-1 transition-transform"></i>
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>
      </div>

      <div className="pt-4 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              <i className="ri-calculator-line mr-3"></i>
              Crypto Calculator
            </h1>
            <p className="text-gray-300 text-lg">
              Real-time cryptocurrency price conversion with live market rates
            </p>
            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-400">
              <i className="ri-time-line"></i>
              <span>Last updated: {lastUpdate.toLocaleTimeString()}</span>
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            </div>
            {error && (
              <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm max-w-md mx-auto">
                <i className="ri-error-warning-line mr-2"></i>
                {error}
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Calculator Card */}
            <div className="lg:col-span-2">
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <i className="ri-exchange-line text-cyan-400"></i>
                  Currency Converter
                </h2>

                {/* From Currency */}
                <div className="mb-6">
                  <label className="text-gray-300 text-sm mb-2 block">From</label>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-4 mb-3">
                      <select
                        value={fromCrypto}
                        onChange={(e) => setFromCrypto(e.target.value)}
                        className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-lg font-semibold cursor-pointer hover:bg-white/20 transition-all pr-8"
                      >
                        {Object.values(rates).map(crypto => (
                          <option key={crypto.symbol} value={crypto.symbol}>
                            {crypto.symbol} - {crypto.name}
                          </option>
                        ))}
                      </select>
                      <div className="text-right">
                        <div className="text-gray-400 text-xs">Price</div>
                        <div className="text-white font-semibold">
                          ${rates[fromCrypto]?.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
                        </div>
                      </div>
                    </div>
                    <input
                      type="number"
                      value={fromAmount}
                      onChange={(e) => setFromAmount(e.target.value)}
                      placeholder="Enter amount"
                      className="w-full bg-transparent border-none text-white text-3xl font-bold outline-none"
                    />
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {quickAmounts.map(amount => (
                        <button
                          key={amount}
                          onClick={() => setFromAmount(amount.toString())}
                          className="px-3 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-gray-300 text-sm transition-all cursor-pointer whitespace-nowrap"
                        >
                          {amount.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Swap Button */}
                <div className="flex justify-center -my-3 relative z-10">
                  <button
                    onClick={handleSwap}
                    className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform cursor-pointer shadow-lg"
                  >
                    <i className="ri-arrow-up-down-line text-xl"></i>
                  </button>
                </div>

                {/* To Currency */}
                <div className="mb-6">
                  <label className="text-gray-300 text-sm mb-2 block">To</label>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-4 mb-3">
                      <select
                        value={toCrypto}
                        onChange={(e) => setToCrypto(e.target.value)}
                        className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-lg font-semibold cursor-pointer hover:bg-white/20 transition-all pr-8"
                      >
                        {Object.values(rates).map(crypto => (
                          <option key={crypto.symbol} value={crypto.symbol}>
                            {crypto.symbol} - {crypto.name}
                          </option>
                        ))}
                      </select>
                      <div className="text-right">
                        <div className="text-gray-400 text-xs">Price</div>
                        <div className="text-white font-semibold">
                          ${rates[toCrypto]?.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
                        </div>
                      </div>
                    </div>
                    <div className="text-white text-3xl font-bold">
                      {toAmount}
                    </div>
                  </div>
                </div>

                {/* Conversion Info */}
                <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-300">Exchange Rate</span>
                    <span className="text-white font-semibold">
                      1 {fromCrypto} = {((rates[fromCrypto]?.price || 0) / (rates[toCrypto]?.price || 1)).toFixed(8)} {toCrypto}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className="text-gray-300">Inverse Rate</span>
                    <span className="text-white font-semibold">
                      1 {toCrypto} = {((rates[toCrypto]?.price || 0) / (rates[fromCrypto]?.price || 1)).toFixed(8)} {fromCrypto}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <button
                    onClick={() => navigate('/pepcoin/buy-sell')}
                    className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-shopping-cart-line mr-2"></i>
                    Buy PepCoin
                  </button>
                  <button
                    onClick={() => navigate('/trading')}
                    className="px-6 py-3 bg-white/5 border border-white/10 text-white rounded-xl font-semibold hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-line-chart-line mr-2"></i>
                    Start Trading
                  </button>
                </div>
              </div>
            </div>

            {/* Live Rates Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <i className="ri-stock-line text-cyan-400"></i>
                    Live Rates
                  </h3>
                  <button
                    onClick={fetchPrices}
                    className="w-8 h-8 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-all cursor-pointer"
                    title="Refresh prices"
                  >
                    <i className="ri-refresh-line"></i>
                  </button>
                </div>
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                  {Object.values(rates).map(crypto => (
                    <div
                      key={crypto.symbol}
                      onClick={() => {
                        setFromCrypto(crypto.symbol);
                        setFromAmount('1');
                      }}
                      className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-3 transition-all cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center">
                            <i className={`${crypto.icon} text-white text-sm`}></i>
                          </div>
                          <div>
                            <div className="text-white font-semibold text-sm">{crypto.symbol}</div>
                            <div className="text-gray-400 text-xs">{crypto.name}</div>
                          </div>
                        </div>
                        <div className={`text-xs font-semibold px-2 py-1 rounded ${
                          crypto.change24h >= 0 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {crypto.change24h >= 0 ? '+' : ''}{crypto.change24h.toFixed(2)}%
                        </div>
                      </div>
                      <div className="text-white font-bold text-lg">
                        ${crypto.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Popular Conversions */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <i className="ri-fire-line text-orange-400"></i>
              Popular Conversions
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { from: 'PEP', to: 'USD', amount: 1000 },
                { from: 'BTC', to: 'USD', amount: 1 },
                { from: 'ETH', to: 'PEP', amount: 1 },
                { from: 'USDT', to: 'PEP', amount: 100 },
              ].map((conv, idx) => {
                const result = (conv.amount * (rates[conv.from]?.price || 0)) / (rates[conv.to]?.price || 1);
                return (
                  <div
                    key={idx}
                    onClick={() => {
                      setFromCrypto(conv.from);
                      setToCrypto(conv.to);
                      setFromAmount(conv.amount.toString());
                    }}
                    className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-4 transition-all cursor-pointer"
                  >
                    <div className="text-gray-400 text-sm mb-2">
                      {conv.amount} {conv.from} to {conv.to}
                    </div>
                    <div className="text-white text-2xl font-bold">
                      {result.toFixed(4)}
                    </div>
                    <div className="text-cyan-400 text-xs mt-2">
                      Click to calculate <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-refresh-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Real-Time Rates</h3>
              <p className="text-gray-400 text-sm">
                Live cryptocurrency prices from CoinGecko API updated every 30 seconds
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Accurate Calculations</h3>
              <p className="text-gray-400 text-sm">
                Precise conversion rates with up to 8 decimal places for maximum accuracy
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-global-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Multiple Currencies</h3>
              <p className="text-gray-400 text-sm">
                Support for 15+ cryptocurrencies and fiat currencies including PepCoin
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CryptoCalculator;
